import React from 'react';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import {
  compact,
  each,
  eq,
  filter,
  find,
  flatten,
  includes,
  intersection,
  isUndefined,
  keys,
  map,
  size,
  split,
  values,
} from 'lodash';

import { Maybe, Nullable } from 'models/types';

import { Inventory } from 'models/sku.model';
import { parse as skuPresentation_parse } from 'api/graphql/SkuPresentation/Gql__SkuPresentation__Decoder__List.bs';
import { Card } from 'models/Models.bs';
import { t as CategoriesData_t } from 'api/Data/CategoriesData.gen';
import { parse as pageContentResolver_parse } from 'utils/PageContentResolver.bs';
import { getTokenOptServer } from 'auth/user/AuthUtils.bs';
import { EMPTY_ELEMENT } from 'common/utils/react';
import SeoMeta from 'components/library/SeoMeta';
import { DrainContextProvider, DrainContextType, DrainType, FilterCurrent, SortValue } from 'contexts/DrainContext';
import { t as SkuPresentation_t } from 'api/graphql/SkuPresentation/Gql__SkuPresentation__Fragment__Decoder.gen';
import getFilterList, { FilterTaxonomy, FilterTaxonomyItem } from 'api/queries/filter';
import { COMMA, EMPTY_STRING } from 'common/utils/string';

import { Card_t } from 'models/Models.gen';
import WallOfCards, { appendDict } from './WallOfCards';
import { NO_CACHE, PRODUCT_SSR_PAGE_CACHE } from 'common/constants';
import { ITaxonomyTerms } from '@kentico/kontent-delivery';

function hasFilterQuery(query: ParsedUrlQuery, filterList: FilterTaxonomy): boolean {
  return size(intersection(keys(filterList.elements), keys(query))) > 0;
}

function findParentFilter(filterValue: string, filterList: FilterTaxonomy): FilterTaxonomyItem {
  let parent = void 0;
  each(keys(filterList.elements), (key: string) => {
    const isContainer =
      size(
        filter(filterList.elements[key].terms, ({ codename }) => {
          return includes(filterValue, codename);
        })
      ) > 0;
    if (isContainer) {
      parent = filterList.elements[key];
    }
  });

  return parent;
}

function findFilterTerm(parentKey: string, filterCodename: string, filterList: FilterTaxonomy): ITaxonomyTerms {
  return find(filterList.elements[parentKey].terms, ({ codename }) => eq(codename, filterCodename));
}

function parseQueryFilter(query: ParsedUrlQuery, filterList: FilterTaxonomy): Maybe<FilterCurrent> {
  if (hasFilterQuery(query, filterList)) {
    const currentFilter = {
      elements: compact(
        flatten(
          map(keys(filterList.elements), (parentKey: string, key: number) => {
            const filter = split(query[parentKey] as string, COMMA);
            // const filter = query[filterKey] as string;
            if (!isUndefined(findParentFilter(filter[0], filterList))) {
              return map(filter, (filterValue: string, valueKey: number) => {
                return {
                  codename: filterValue,
                  name: findFilterTerm(parentKey, filterValue, filterList).name,
                  parent: {
                    codename: parentKey,
                    name: findParentFilter(filterValue, filterList).name,
                    parent: void 0,
                  },
                };
              });
            } else {
              return void 0;
            }
          })
        )
      ),
    };
    return currentFilter;
  } else {
    return void 0;
  }
}

function parseQuerySort(query: ParsedUrlQuery): SortValue {
  return query?.sort && includes(values(SortValue), query?.sort) ? (query?.sort as SortValue) : SortValue.Featured;
}

type Props = {
  inventory: Nullable<Inventory>;
  categories: CategoriesData_t;
  content: Nullable<string>;
  skuList: Nullable<string>;
  filterList: FilterTaxonomy;
};

export const StorePage: React.FC<Props> = ({ inventory, categories, content, skuList, filterList }) => {
  const currentInventory =
    inventory?.skus?.map(sku =>
      Card.skuToCard({
        ...sku,
        isPbCategoryHidden: sku.hideFromAllBusinessCategories,
        isPcCategoryHidden: sku.hideFromAllClassicCategories,
      })
    ) || [];

  const skuDict = skuPresentation_parse(skuList)?.map((dict: any) => [dict.codename, dict]);

  const seoMetaContent = pageContentResolver_parse(content)?.seo_meta;
  const seoMeta = seoMetaContent ? <SeoMeta meta={seoMetaContent} /> : EMPTY_ELEMENT;

  const storeContent = pageContentResolver_parse(content)?.store_content;
  const store = storeContent || void 0;

  const router = useRouter();
  const initDefaultDrainContextProviderValue: (
    inventory: Card_t[],
    skuDict: Maybe<Array<[string, SkuPresentation_t]>>
  ) => DrainContextType<Card_t> = (inventory: Card_t[], skuDict: Maybe<Array<[string, SkuPresentation_t]>>) => {
    const cardList = appendDict(skuDict)(inventory);
    return {
      filter: parseQueryFilter(router.query, filterList),
      prevFilter: void 0,
      isOpen: false,
      currentType: DrainType.Filter,
      // @note: router query param can be an array
      search: (router.query?.search as string) || EMPTY_STRING,
      setPage: () => void 0,
      appendFilter: () => void 0,
      setSearch: () => void 0,
      onSearchChange: () => void 0,
      hasSearch: () => false,
      switchFilter: () => void 0,
      page: void 0,
      applyFilter: () => void 0,
      setFilter: () => void 0,
      apply: hasFilterQuery(router.query, filterList),
      hasFilter: () => false,
      setSortValue: () => void 0,
      applySort: () => void 0,
      sort: parseQuerySort(router.query),
      prevSort: SortValue.Featured, // @note: "Featured" is the default sort value
      initValue: () => void 0,
      hasSort: () => false,
      currentValue: {
        current: cardList,
        cardList,
        filter: {
          ...parseQueryFilter(router.query, filterList),
        },
        sort: SortValue.Featured,
      },
      clearFilter: () => void 0,
      restore: () => void 0,
      applySearch: () => void 0,
      setCurrentValue: () => void 0,
      setPrevFilter: () => void 0,
      trackDrainFilterSection: () => void 0,
    };
  };

  /**
   * Init Drain Context Value
   */
  const drainContextValue = initDefaultDrainContextProviderValue(currentInventory, skuDict);

  return (
    <>
      <DrainContextProvider value={drainContextValue}>
        {seoMeta}
        <WallOfCards inventoryProps={currentInventory} storeContent={store} skuDict={skuDict} filterList={filterList} />
      </DrainContextProvider>
    </>
  );
};
